import React, { useState, useEffect } from 'react';
import { useUser } from '../AuthPage/UserContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { getProfilePhotoSrc, PLACEHOLDER_DATA_URI } from '../../../utils/profilePhoto';
import ConfirmDialog from '../../common/ConfirmDialog/ConfirmDialog';
import AddressInput from '../../common/AddressInput/AddressInput';
import AvatarFrame from '../../common/AvatarFrame/AvatarFrame';
import Icon from '../../common/Icon/Icon';
import Experience from '../../pages/Experience/Experience';
import BlockedTraders from './BlockedTraders';
import './AccountSettings.css';

/**
 * Account settings: Profile (username, email verification, avatar),
 * Security (password, log out everywhere), Preferences (saved delivery
 * address, trade default, notifications, theme), Privacy & Data
 * (binder visibility, anonymizing account deletion).
 */
function AccountSettings() {
    const { user, setUser } = useUser();
    const { theme, toggleTheme } = useTheme();
    // /account?tab=preferences deep-links a section (e.g. search's
    // "Set your location" button)
    const [activeTab, setActiveTab] = useState(() => {
        const t = new URLSearchParams(window.location.search).get('tab');
        return ['profile', 'experience', 'security', 'preferences', 'privacy'].includes(t) ? t : 'profile';
    });
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [dialog, setDialog] = useState(null);

    // Profile
    const [avatarFile, setAvatarFile] = useState(null);
    const [avatarPreview, setAvatarPreview] = useState('');
    const [emailVerified, setEmailVerified] = useState(null);
    const [verifySent, setVerifySent] = useState(false);

    // Security
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '', newPassword: '', confirmPassword: ''
    });

    // Preferences / privacy (server-backed)
    const [settings, setSettings] = useState(null);
    const [addressInput, setAddressInput] = useState('');
    const [locationInput, setLocationInput] = useState('');
    // Map verification (AddressInput): Save unlocks once the pin is confirmed
    const [locVerified, setLocVerified] = useState(false);
    const [addrVerified, setAddrVerified] = useState(false);
    const [showDelivery, setShowDelivery] = useState(false);
    const [savingSettings, setSavingSettings] = useState(false);
    const [shops, setShops] = useState([]);

    // Danger zone
    const [deletePassword, setDeletePassword] = useState('');
    const [deleteConfirmText, setDeleteConfirmText] = useState('');

    useEffect(() => {
        fetch('/api/auth/me', { credentials: 'include' })
            .then(r => (r.ok ? r.json() : null))
            .then(d => setEmailVerified(d?.user?.is_email_verified === 1))
            .catch(() => { });
        fetch('/api/user/settings', { credentials: 'include' })
            .then(r => (r.ok ? r.json() : null))
            .then(d => {
                if (d?.settings) {
                    setSettings(d.settings);
                    setAddressInput(d.settings.default_address || '');
                    setLocationInput(d.settings.location_text || '');
                    setShowDelivery(!!d.settings.default_address);
                }
            })
            .catch(() => { });
        fetch('/api/shops')
            .then(r => (r.ok ? r.json() : { shops: [] }))
            .then(d => setShops(d.shops || []))
            .catch(() => { });
    }, []);

    function maskEmail(email) {
        if (!email) return '';
        const [name, domain] = email.split('@');
        if (name.length <= 3) return name + '...@' + domain;
        return name.slice(0, 3) + '...@' + domain;
    }

    const flash = (type, text) => setMessage({ type, text });

    // ---------- Profile ----------
    const handleAvatarChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) return flash('error', 'Image must be under 5MB.');
        if (!file.type.startsWith('image/')) return flash('error', 'Choose a valid image file.');
        setAvatarFile(file);
        const reader = new FileReader();
        reader.onload = ev => setAvatarPreview(ev.target.result);
        reader.readAsDataURL(file);
    };

    const handleAvatarUpload = async () => {
        if (!avatarFile) return;
        setIsLoading(true);
        try {
            const formData = new FormData();
            formData.append('photo', avatarFile);
            const response = await fetch('/api/users/profile-photo', {
                method: 'POST', credentials: 'include', body: formData
            });
            if (response.ok) {
                const data = await response.json();
                setUser(data.user);
                setAvatarPreview('');
                setAvatarFile(null);
                flash('success', 'Profile photo updated.');
            } else {
                const error = await response.json();
                flash('error', error.error || 'Failed to upload photo.');
            }
        } catch {
            flash('error', 'Failed to upload photo.');
        } finally {
            setIsLoading(false);
        }
    };

    const resendVerification = async () => {
        try {
            const res = await fetch('/api/auth/verify-email/request', {
                method: 'POST', credentials: 'include'
            });
            const d = await res.json();
            if (!res.ok) throw new Error(d.error || 'Failed to send.');
            setVerifySent(true);
            flash('success', d.alreadyVerified ? 'Your email is already verified.' : 'Verification email sent. Check your inbox.');
        } catch (e) {
            flash('error', e.message);
        }
    };

    // ---------- Security ----------
    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            return flash('error', 'New passwords do not match.');
        }
        setIsLoading(true);
        setMessage({ type: '', text: '' });
        try {
            const response = await fetch('/api/user/password', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    currentPassword: passwordForm.currentPassword,
                    newPassword: passwordForm.newPassword
                })
            });
            if (response.ok) {
                flash('success', 'Password updated.');
                setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
            } else {
                const error = await response.json();
                flash('error', error.error || 'Failed to update password.');
            }
        } catch {
            flash('error', 'Failed to update password.');
        } finally {
            setIsLoading(false);
        }
    };

    const logoutEverywhere = () => setDialog({
        title: 'Log out everywhere?',
        message: 'Every signed-in device, including this one, will need to log in again.',
        confirmLabel: 'Log out everywhere',
        action: async () => {
            try {
                await fetch('/api/user/logout-all', { method: 'POST', credentials: 'include' });
            } catch { }
            setUser(null);
            window.location.href = '/auth';
        },
    });

    // ---------- Preferences ----------
    const saveSettings = async (patch) => {
        setSavingSettings(true);
        setMessage({ type: '', text: '' });
        try {
            const res = await fetch('/api/user/settings', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(patch)
            });
            const d = await res.json();
            if (!res.ok) throw new Error(d.error || 'Failed to save.');
            setSettings(d.settings);
            setAddressInput(d.settings.default_address || '');
            setLocationInput(d.settings.location_text || '');
            setLocVerified(false);
            setAddrVerified(false);
            flash('success', 'Saved.');
            return true;
        } catch (e) {
            flash('error', e.message);
            return false;
        } finally {
            setSavingSettings(false);
        }
    };

    const toggle = (key) => {
        if (!settings) return;
        saveSettings({ [key]: settings[key] ? 0 : 1 });
    };

    // ---------- Danger ----------
    const handleDeleteAccount = () => {
        if (deleteConfirmText.trim().toUpperCase() !== 'DELETE') {
            return flash('error', 'Type DELETE to confirm.');
        }
        if (!deletePassword) return flash('error', 'Enter your password.');
        setDialog({
            title: 'Delete your account?',
            message: 'Your profile is anonymized and your binders are wiped. Completed trades stay visible to their other participants. This cannot be undone.',
            confirmLabel: 'Delete my account',
            danger: true,
            action: async () => {
                setIsLoading(true);
                try {
                    const res = await fetch('/api/user/delete', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ password: deletePassword })
                    });
                    const d = await res.json();
                    if (!res.ok) throw new Error(d.error || 'Failed to delete account.');
                    setUser(null);
                    window.location.href = '/';
                } catch (e) {
                    flash('error', e.message);
                    setIsLoading(false);
                }
            },
        });
    };

    const tabs = [
        { id: 'profile', label: 'Profile' },
        { id: 'experience', label: 'Experience' },
        { id: 'security', label: 'Security' },
        { id: 'preferences', label: 'Preferences' },
        { id: 'privacy', label: 'Privacy & Data' }
    ];

    return (
        <div className="account-settings">
            <header className="page-section-head">
                <h1>Account <span className="page-section-current">{tabs.find(t => t.id === activeTab)?.label}</span></h1>
            </header>
            <div className="account-settings-container">
                <div className="account-settings-content">
                    <div className="account-settings-sidebar">
                        <nav className="account-settings-nav">
                            {tabs.map(tab => (
                                <button
                                    key={tab.id}
                                    className={`nav-tab ${activeTab === tab.id ? 'active' : ''}`}
                                    onClick={() => {
                                        setActiveTab(tab.id);
                                        setMessage({ type: '', text: '' });
                                    }}
                                >
                                    <span className="tab-label">{tab.label}</span>
                                </button>
                            ))}
                        </nav>
                    </div>

                    <div className="account-settings-main">
                        {message.text && (
                            <div className={`message ${message.type}`}>{message.text}</div>
                        )}

                        {activeTab === 'profile' && (
                            <>
                                <div className="settings-section">
                                    <h2 className="settings-title">Profile</h2>
                                    <form className="settings-form" autoComplete="off" onSubmit={e => e.preventDefault()}>
                                        <div className="form-group">
                                            <label>Username</label>
                                            <div className="settings-inline-value">{user?.username}</div>
                                            <small className="settings-hint">
                                                Usernames are permanent. They're how other traders recognize you
                                                across trades and reviews.
                                            </small>
                                        </div>
                                        <div className="form-group">
                                            <label>Email</label>
                                            <div className="settings-inline-value">
                                                {maskEmail(user?.email || '')}
                                                {emailVerified === true && <span className="email-chip verified">✓ verified</span>}
                                                {emailVerified === false && <span className="email-chip unverified">not verified</span>}
                                            </div>
                                            {emailVerified === false && (
                                                <button
                                                    type="button"
                                                    className="btn-primary"
                                                    style={{ width: 'fit-content' }}
                                                    onClick={resendVerification}
                                                    disabled={verifySent}
                                                >
                                                    {verifySent ? 'Sent. Check your inbox' : 'Send verification email'}
                                                </button>
                                            )}
                                        </div>
                                    </form>
                                </div>

                                <div className="settings-section" style={{ marginTop: '2rem' }}>
                                    <h2 className="settings-title">Profile Picture</h2>
                                    <div className="avatar-upload" style={{ padding: '1rem 0' }}>
                                        <div className="avatar-preview">
                                            <AvatarFrame user={user}>
                                                <img
                                                    key={user?.profile_photo || 'placeholder'}
                                                    src={avatarPreview || getProfilePhotoSrc(user)}
                                                    alt="Profile"
                                                    className="avatar-image"
                                                    onError={e => { e.target.onerror = null; e.target.src = PLACEHOLDER_DATA_URI; }}
                                                />
                                            </AvatarFrame>
                                        </div>
                                        <div className="avatar-upload-controls">
                                            <label className="btn-upload" style={{ cursor: 'pointer' }}>
                                                Choose image
                                                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarChange} />
                                            </label>
                                            {avatarFile && (
                                                <button type="button" className="btn-primary" style={{ marginLeft: 8 }}
                                                    onClick={handleAvatarUpload} disabled={isLoading}>
                                                    {isLoading ? 'Uploading…' : 'Save photo'}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Level + border unlocks (was its own page) */}
                        {activeTab === 'experience' && <Experience embedded />}

                        {activeTab === 'security' && (
                            <>
                                <div className="settings-section">
                                    <h2>Change Password</h2>
                                    <form onSubmit={handlePasswordSubmit} className="settings-form">
                                        <div className="form-group">
                                            <label htmlFor="currentPassword">Current password</label>
                                            <input
                                                type="password"
                                                id="currentPassword"
                                                value={passwordForm.currentPassword}
                                                onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="newPassword">New password</label>
                                            <input
                                                type="password"
                                                id="newPassword"
                                                value={passwordForm.newPassword}
                                                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                                                required
                                            />
                                            <small className="settings-hint">8+ characters, with a number and a special character.</small>
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="confirmPassword">Confirm new password</label>
                                            <input
                                                type="password"
                                                id="confirmPassword"
                                                value={passwordForm.confirmPassword}
                                                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                                                required
                                            />
                                        </div>
                                        <button type="submit" className="btn-primary" disabled={isLoading}>
                                            {isLoading ? 'Updating…' : 'Update password'}
                                        </button>
                                    </form>
                                </div>

                                <div className="settings-section" style={{ marginTop: '2rem' }}>
                                    <h2>Sessions</h2>
                                    <p className="settings-hint">
                                        Lost a device or logged in somewhere you don't recognize?
                                    </p>
                                    <button type="button" className="btn-danger" onClick={logoutEverywhere}>
                                        Log out everywhere
                                    </button>
                                </div>
                            </>
                        )}

                        {activeTab === 'preferences' && (
                            <>
                                <div className="settings-section">
                                    <h2>Location</h2>
                                    <p className="settings-hint">
                                        Optional. Shown on your profile and powers "Near me" in search for local
                                        trades. City and state, or just a state. Locate it on the map, then save.
                                    </p>
                                    <div className="form-group">
                                        <AddressInput
                                            address={locationInput}
                                            setAddress={setLocationInput}
                                            verified={locVerified}
                                            setVerified={setLocVerified}
                                            placeholder='e.g. "Austin, TX" or "Texas"'
                                            note="Only this city/state is shown to other traders."
                                        />
                                        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                            <button type="button" className="btn-primary"
                                                disabled={savingSettings || !locVerified || locationInput.trim() === (settings?.location_text || '')}
                                                onClick={() => saveSettings({ location_text: locationInput })}>
                                                {savingSettings ? 'Saving…' : 'Save location'}
                                            </button>
                                            {settings?.location_text && (
                                                <button type="button" className="btn-upload"
                                                    onClick={() => saveSettings({ location_text: '' })}>
                                                    Clear
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="settings-section" style={{ marginTop: '2rem' }}>
                                    <h2>
                                        <button type="button" className="settings-collapse" onClick={() => setShowDelivery(v => !v)}>
                                            {showDelivery ? '▾' : '▸'} Delivery Address
                                        </button>
                                    </h2>
                                    {!showDelivery && (
                                        <p className="settings-hint">Optional. Saved once, prefilled in every trade's shipping setup.</p>
                                    )}
                                    {showDelivery && (<>
                                    <p className="settings-hint">
                                        Saved once, prefilled in every trade's shipping setup. Verified on save.
                                    </p>
                                    <div className="form-group">
                                        <AddressInput
                                            address={addressInput}
                                            setAddress={setAddressInput}
                                            verified={addrVerified}
                                            setVerified={setAddrVerified}
                                        />
                                        {settings?.addr_resolved && settings.default_address === addressInput && (
                                            <small className="settings-hint">Verified as: {settings.addr_resolved}</small>
                                        )}
                                        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                            <button type="button" className="btn-primary"
                                                disabled={savingSettings || !addrVerified || addressInput.trim() === (settings?.default_address || '')}
                                                onClick={() => saveSettings({ default_address: addressInput })}>
                                                {savingSettings ? 'Saving…' : 'Save address'}
                                            </button>
                                            {settings?.default_address && (
                                                <button type="button" className="btn-upload"
                                                    onClick={() => saveSettings({ default_address: '' })}>
                                                    Clear
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    </>)}
                                </div>


                                <div className="settings-section" style={{ marginTop: '2rem' }}>
                                    <h2>Trading</h2>
                                    <div className="form-group">
                                        <label htmlFor="mmDefault">Middleman preference</label>
                                        <select
                                            id="mmDefault"
                                            value={settings?.mm_default || 'ask'}
                                            onChange={e => saveSettings({ mm_default: e.target.value })}
                                        >
                                            <option value="ask">Ask me on each trade</option>
                                            <option value="yes">Prefer a middleman</option>
                                            <option value="no">Prefer shipping directly</option>
                                        </select>
                                        <small className="settings-hint">
                                            Applied automatically when a trade is accepted. You still review and lock in each step.
                                        </small>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="preferredShop">Preferred middleman</label>
                                        <select
                                            id="preferredShop"
                                            value={settings?.preferred_shop_id || ''}
                                            onChange={e => saveSettings({ preferred_shop_id: e.target.value ? Number(e.target.value) : null })}
                                        >
                                            <option value="">None chosen</option>
                                            {shops.map(s => (
                                                <option key={s.id} value={s.id}>
                                                    {s.name}{s.rating_avg ? ` (★ ${s.rating_avg})` : ''}
                                                </option>
                                            ))}
                                        </select>
                                        <small className="settings-hint">
                                            Prefilled as your shop in trade setups. With this, the address, and a middleman
                                            preference set, accepted trades fill their first two steps for you.
                                        </small>
                                    </div>
                                </div>

                                <div className="settings-section" style={{ marginTop: '2rem' }}>
                                    <h2>Notifications</h2>
                                    {settings ? (
                                        <div className="settings-form">
                                            <div className="form-group">
                                                <label className="checkbox-label">
                                                    <input type="checkbox" checked={!!settings.notify_offers} onChange={() => toggle('notify_offers')} />
                                                    <span className="checkmark"></span>
                                                    Trade offers (new, countered, accepted)
                                                </label>
                                            </div>
                                            <div className="form-group">
                                                <label className="checkbox-label">
                                                    <input type="checkbox" checked={!!settings.notify_shipping} onChange={() => toggle('notify_shipping')} />
                                                    <span className="checkmark"></span>
                                                    Shipping updates
                                                </label>
                                                <small className="settings-hint">Turning this off hides "your turn" shipping badges too.</small>
                                            </div>
                                            <div className="form-group">
                                                <label className="checkbox-label">
                                                    <input type="checkbox" checked={!!settings.notify_completed} onChange={() => toggle('notify_completed')} />
                                                    <span className="checkmark"></span>
                                                    Completed and cancelled trades
                                                </label>
                                            </div>
                                            <div className="form-group">
                                                <label className="checkbox-label">
                                                    <input type="checkbox" checked={!!settings.email_notifications} onChange={() => toggle('email_notifications')} />
                                                    <span className="checkmark"></span>
                                                    Also send me emails
                                                </label>
                                                <small className="settings-hint">Requires a verified email address.</small>
                                            </div>
                                        </div>
                                    ) : <p className="settings-hint">Loading…</p>}
                                </div>

                                <div className="settings-section" style={{ marginTop: '2rem' }}>
                                    <h2>Appearance</h2>
                                    <button type="button" className="theme-toggle-btn" onClick={toggleTheme}>
                                        <span className="theme-toggle-icon"><Icon name={theme === 'light' ? 'moon' : 'sun'} size={18} /></span>
                                        <span>{theme === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}</span>
                                    </button>
                                </div>

                                <div className="settings-section" style={{ marginTop: '2rem' }}>
                                    <h2>Middleman Program</h2>
                                    <p className="settings-hint">
                                        Run a card shop? Verified middlemen receive, check, and forward both
                                        sides of a trade, with their own store page, ratings, and fees.
                                        Applications are reviewed by hand; an application processing fee may apply.
                                    </p>
                                    <button
                                        type="button"
                                        className="btn-primary"
                                        style={{ width: 'fit-content' }}
                                        onClick={() => window.location.assign('/contact?topic=middleman')}
                                    >
                                        Apply to become a middleman
                                    </button>
                                </div>
                            </>
                        )}

                        {activeTab === 'privacy' && (
                            <>
                                <div className="settings-section">
                                    <h2>Privacy</h2>
                                    <div className="form-group">
                                        <label htmlFor="privacyBinders">Who can see your binders</label>
                                        <select
                                            id="privacyBinders"
                                            value={settings?.privacy_binders || 'public'}
                                            onChange={e => saveSettings({ privacy_binders: e.target.value })}
                                        >
                                            <option value="public">Everyone</option>
                                            <option value="users">Logged-in traders</option>
                                            <option value="private">Only me</option>
                                        </select>
                                        <small className="settings-hint">
                                            Private binders don't appear on your profile, but you can still trade normally.
                                        </small>
                                    </div>
                                </div>

                                <BlockedTraders />

                                <div className="settings-section danger-zone" style={{ marginTop: '2rem' }}>
                                    <h2>Danger Zone</h2>
                                    <div className="danger-actions">
                                        <div className="danger-action">
                                            <h3>Delete account</h3>
                                            <p>
                                                Your profile is anonymized and your binders removed.
                                                Completed trades remain visible to their other participants.
                                            </p>
                                            <div className="form-group">
                                                <label htmlFor="deletePassword">Your password</label>
                                                <input
                                                    type="password"
                                                    id="deletePassword"
                                                    value={deletePassword}
                                                    onChange={e => setDeletePassword(e.target.value)}
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label htmlFor="deleteConfirm">Type DELETE to confirm</label>
                                                <input
                                                    type="text"
                                                    id="deleteConfirm"
                                                    value={deleteConfirmText}
                                                    onChange={e => setDeleteConfirmText(e.target.value)}
                                                />
                                            </div>
                                            <button
                                                onClick={handleDeleteAccount}
                                                className="btn-danger"
                                                disabled={isLoading}
                                            >
                                                {isLoading ? 'Deleting…' : 'Delete account'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <ConfirmDialog
                open={!!dialog}
                title={dialog?.title}
                message={dialog?.message}
                confirmLabel={dialog?.confirmLabel}
                danger={dialog?.danger}
                onConfirm={() => { const a = dialog.action; setDialog(null); a(); }}
                onCancel={() => setDialog(null)}
            />
        </div>
    );
}

export default AccountSettings;
